import React, { useState, useContext, useMemo, useEffect } from "react";
import { AuthContext } from "../../../contexts/AuthContext";
import Header from "../../../layout/Header/Header";
import { Button, Row, Col, Table, Input, Label, Modal, ModalHeader, ModalBody, ModalFooter } from "reactstrap";
import { jsPDF } from "jspdf";
import "jspdf-autotable";
import ExportJsonExcel from "js-export-excel";
import axios from "axios";
import moment from "moment";
import { Search } from "../../../DataTable";

// Antiguedad de saldos (portado de AmericanFly, tickets 28 OCT 2026).
// Rangos por dias vencidos: fecha de la nota + plazo del cliente.
const RANGOS = [
  { k: "enTiempo", l: "En tiempo" },
  { k: "treinta", l: "1-30" },
  { k: "sesenta", l: "31-60" },
  { k: "noventa", l: "61-90" },
  { k: "cientoveinte", l: "91-120" },
  { k: "cientoochenta", l: "121-180" },
  { k: "otro", l: "+180" },
];

const pesos = (n) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 2 }).format(n || 0);

function AntiguedadSaldosVencido() {
  const { user } = useContext(AuthContext);
  const URL_DASHBOARDS = process.env.REACT_APP_URL_DASHBOARDS;

  const [datos, setDatos] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [search, setSearch] = useState("");
  const [vendedor, setVendedor] = useState("");
  const [soloVencido, setSoloVencido] = useState(false);
  const [orden, setOrden] = useState({ campo: "totalGeneral", dir: -1 });

  const [modalDetalle, setModalDetalle] = useState(false);
  const toggleDetalle = () => setModalDetalle(!modalDetalle);
  const [detalle, setDetalle] = useState(null);

  useEffect(() => {
    axios
      .get(`${URL_DASHBOARDS}AntiguedadSaldosVencido`, {
        headers: { Authorization: `Bearer: ${localStorage.getItem("app_token")}` },
      })
      .then((r) => setDatos(r.data))
      .catch((err) => console.log(err))
      .finally(() => setCargando(false));
  }, []);

  const vendedores = useMemo(
    () => [...new Set(datos.map((d) => d.vendedor).filter(Boolean))].sort(),
    [datos]
  );

  const filas = useMemo(() => {
    const s = search.toLowerCase();
    let f = datos.filter(
      (d) =>
        (!s || String(d.nombreCliente ?? "").toLowerCase().includes(s) || String(d.vendedor ?? "").toLowerCase().includes(s)) &&
        (!vendedor || d.vendedor === vendedor)
    );
    if (soloVencido) f = f.filter((d) => d.totalGeneral - d.enTiempo > 0.009);
    return [...f].sort((a, b) =>
      orden.campo === "nombreCliente"
        ? orden.dir * String(a.nombreCliente).localeCompare(String(b.nombreCliente))
        : orden.dir * ((a[orden.campo] || 0) - (b[orden.campo] || 0))
    );
  }, [datos, search, vendedor, soloVencido, orden]);

  const totales = useMemo(() => {
    const t = { totalGeneral: 0 };
    RANGOS.forEach((r) => (t[r.k] = 0));
    filas.forEach((f) => {
      RANGOS.forEach((r) => (t[r.k] += f[r.k] || 0));
      t.totalGeneral += f.totalGeneral || 0;
    });
    return t;
  }, [filas]);

  const ordena = (campo) =>
    setOrden((o) => ({ campo, dir: o.campo === campo ? -o.dir : campo === "nombreCliente" ? 1 : -1 }));
  const flecha = (campo) => (orden.campo === campo ? (orden.dir === 1 ? " ▲" : " ▼") : "");

  function abreDetalle(cliente, rango) {
    const docs = [];
    const rangos = rango ? [rango] : RANGOS.map((r) => r.k);
    rangos.forEach((k) => {
      (cliente.surtidos[k] || []).forEach((d) => docs.push({ ...d, tipo: "Nota", rango: k }));
      (cliente.notasCargo[k] || []).forEach((d) => docs.push({ ...d, tipo: "Nota de cargo", rango: k }));
    });
    docs.sort((a, b) => (a.fechaVencimiento > b.fechaVencimiento ? 1 : -1));
    setDetalle({
      cliente,
      rango: rango ? RANGOS.find((r) => r.k === rango).l : "Todos",
      docs,
    });
    setModalDetalle(true);
  }

  function excel() {
    const option = {
      fileName: `AntiguedadSaldos-${moment().format("YYYY-MM-DD")}`,
      datas: [
        {
          sheetData: filas.map((f) => {
            const fila = { Cliente: f.nombreCliente, Vendedor: f.vendedor, Plazo: f.plazo };
            RANGOS.forEach((r) => (fila[r.l] = Number((f[r.k] || 0).toFixed(2))));
            fila.Total = Number((f.totalGeneral || 0).toFixed(2));
            return fila;
          }),
          sheetName: "Antiguedad",
          sheetFilter: ["Cliente", "Vendedor", "Plazo", ...RANGOS.map((r) => r.l), "Total"],
          sheetHeader: ["Cliente", "Vendedor", "Plazo", ...RANGOS.map((r) => r.l), "Total"],
        },
      ],
    };
    new ExportJsonExcel(option).saveExcel();
  }

  function pdf() {
    const doc = new jsPDF({ orientation: "landscape", compressPdf: true });
    const img = new Image();
    img.src = process.env.PUBLIC_URL + "logoInstitucion.png";
    doc.addImage(img, "png", 10, 6, 15, 15);
    doc.setFontSize(14);
    doc.text("Antiguedad de Saldos", 30, 13);
    doc.setFontSize(9);
    doc.text(`Al ${moment().format("DD/MM/YYYY")}${vendedor ? ` · Vendedor: ${vendedor}` : ""}`, 30, 19);
    doc.autoTable({
      head: [["Cliente", "Vendedor", "Plazo", ...RANGOS.map((r) => r.l), "Total"]],
      body: filas.map((f) => [
        f.nombreCliente,
        f.vendedor,
        f.plazo,
        ...RANGOS.map((r) => pesos(f[r.k])),
        pesos(f.totalGeneral),
      ]),
      foot: [["", "", "Totales", ...RANGOS.map((r) => pesos(totales[r.k])), pesos(totales.totalGeneral)]],
      showFoot: "lastPage",
      styles: { fontSize: 7 },
      footStyles: { fontSize: 7, fillColor: [255, 255, 255], textColor: [0, 0, 0] },
      margin: { left: 5, right: 5 },
      startY: 25,
    });
    doc.save(`AntiguedadSaldos-${moment().format("YYYY-MM-DD")}.pdf`);
  }

  const celda = { textAlign: "right", cursor: "pointer", whiteSpace: "nowrap" };
  // theme.css pinta el tfoot en blanco: los totales llevan color explicito
  const pie = { color: "#0f172a", fontWeight: 700, backgroundColor: "#f1f5f9" };

  return (
    <>
      <Header />
      <br />
      <br />
      {user.edoCtaClientes || user.abonos ? (
        <div className="card container col-12">
          <Row>
            <Col md={8}>
              <Button size="sm" href="/MenuAdmin" className="btn btn-danger" id="botonListado">
                Regresar
              </Button>
            </Col>
            <Col sm={4}>
              <ButtonGroupExport onPdf={pdf} onExcel={excel} />
            </Col>
          </Row>
          <h3 align="center">Antiguedad de Saldos</h3>
          <Row>
            <Col md={3}>
              <Label>Vendedor</Label>
              <Input bsSize="sm" type="select" value={vendedor} onChange={(e) => setVendedor(e.target.value)}>
                <option value="">Todos</option>
                {vendedores.map((v) => (
                  <option value={v} key={v}>
                    {v}
                  </option>
                ))}
              </Input>
            </Col>
            <Col md={3} style={{ paddingTop: "30px" }}>
              <label>
                <input type="checkbox" checked={soloVencido} onChange={(e) => setSoloVencido(e.target.checked)} />{" "}
                Solo clientes con saldo vencido
              </label>
            </Col>
            <Col md={6} className="d-flex flex-row-reverse" style={{ paddingTop: "26px" }}>
              <Search onSearch={(v) => setSearch(v)} />
            </Col>
          </Row>
          <br />
          {cargando ? (
            <p>Cargando...</p>
          ) : (
            <Table size="sm" striped borderless className="table-responsive-xl">
              <thead>
                <tr>
                  <th style={{ cursor: "pointer" }} onClick={() => ordena("nombreCliente")}>
                    Cliente{flecha("nombreCliente")}
                  </th>
                  <th>Vendedor</th>
                  <th>Plazo</th>
                  {RANGOS.map((r) => (
                    <th key={r.k} style={{ textAlign: "right", cursor: "pointer" }} onClick={() => ordena(r.k)}>
                      {r.l}
                      {flecha(r.k)}
                    </th>
                  ))}
                  <th style={{ textAlign: "right", cursor: "pointer" }} onClick={() => ordena("totalGeneral")}>
                    Total{flecha("totalGeneral")}
                  </th>
                </tr>
              </thead>
              <tbody>
                {filas.map((f) => (
                  <tr key={f.clienteId}>
                    <td style={{ cursor: "pointer" }} onClick={() => abreDetalle(f)} title="Ver todas sus notas">
                      {f.nombreCliente}
                    </td>
                    <td>{f.vendedor}</td>
                    <td>{f.plazo} dias</td>
                    {RANGOS.map((r) => (
                      <td
                        key={r.k}
                        style={{ ...celda, color: r.k !== "enTiempo" && f[r.k] > 0 ? "#dc2626" : undefined }}
                        onClick={() => f[r.k] > 0 && abreDetalle(f, r.k)}
                      >
                        {f[r.k] > 0 ? pesos(f[r.k]) : "-"}
                      </td>
                    ))}
                    <td style={{ ...celda, fontWeight: 700 }} onClick={() => abreDetalle(f)}>
                      {pesos(f.totalGeneral)}
                    </td>
                  </tr>
                ))}
                {!filas.length ? (
                  <tr>
                    <td colSpan={RANGOS.length + 4} style={{ color: "#64748b" }}>
                      No hay saldos pendientes.
                    </td>
                  </tr>
                ) : null}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan={3} align="right" style={pie}>
                    Totales ({filas.length} clientes)
                  </td>
                  {RANGOS.map((r) => (
                    <td key={r.k} style={{ ...celda, ...pie }}>
                      {pesos(totales[r.k])}
                    </td>
                  ))}
                  <td style={{ ...celda, ...pie }}>{pesos(totales.totalGeneral)}</td>
                </tr>
              </tfoot>
            </Table>
          )}
        </div>
      ) : (
        <div className="card container">
          <p>No tienes permiso para ver esta pantalla.</p>
        </div>
      )}

      <Modal size="lg" isOpen={modalDetalle} toggle={toggleDetalle}>
        <ModalHeader toggle={toggleDetalle}>
          <h4>{detalle?.cliente.nombreCliente}</h4>
          <small style={{ color: "#64748b" }}>
            Rango: {detalle?.rango} · Plazo {detalle?.cliente.plazo} dias (corre desde el acuse; sin acuse, desde la nota)
          </small>
        </ModalHeader>
        <ModalBody>
          <Table size="sm" striped borderless>
            <thead>
              <tr>
                <th>Tipo</th>
                <th>Folio</th>
                <th>Pedido</th>
                <th>Fecha</th>
                <th>Acuse</th>
                <th>Vence</th>
                <th style={{ textAlign: "right" }}>Dias vencido</th>
                <th style={{ textAlign: "right" }}>Total</th>
                <th style={{ textAlign: "right" }}>Saldo</th>
              </tr>
            </thead>
            <tbody>
              {(detalle?.docs || []).map((d) => (
                <tr key={d._id}>
                  <td>{d.tipo}</td>
                  <td>{d.folio}</td>
                  <td>{d.pedido}</td>
                  <td>{d.fecha}</td>
                  <td style={d.tipo === "Nota" && !d.acuse ? { color: "#dc2626" } : undefined}>
                    {d.tipo === "Nota" ? d.acuse || "Sin acuse" : ""}
                  </td>
                  <td>{d.fechaVencimiento}</td>
                  <td align="right" style={d.diasVencido > 0 ? { color: "#dc2626" } : undefined}>
                    {d.diasVencido}
                  </td>
                  <td align="right">{pesos(d.total_general)}</td>
                  <td align="right">{pesos(d.saldo)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <td colSpan={8} align="right" style={pie}>
                  Saldo
                </td>
                <td align="right" style={pie}>{pesos((detalle?.docs || []).reduce((t, d) => t + (d.saldo || 0), 0))}</td>
              </tr>
            </tfoot>
          </Table>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={toggleDetalle}>
            Cerrar
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
}

function ButtonGroupExport({ onPdf, onExcel }) {
  return (
    <div className="d-flex flex-row-reverse" style={{ gap: "4px" }}>
      <Button size="sm" className="btn" color="primary" onClick={onExcel}>
        Excel <i class="far fa-file-excel"></i>
      </Button>
      <Button size="sm" className="btn" color="danger" onClick={onPdf}>
        PDF <i class="far fa-file-pdf"></i>
      </Button>
    </div>
  );
}

export default AntiguedadSaldosVencido;
